import React from "react";
import Grid from "@material-ui/core/Grid";
import { useFormik } from "formik";
import Toolbar from "@material-ui/core/Toolbar";
import { PrimaryButton, SecondaryButton } from "@prestosports/commons-ui";
import TextField from "@material-ui/core/TextField";
import * as Yup from "yup";
import { ColorPicker } from "material-ui-color";
import Box from "@material-ui/core/Box";
import teamDetailsStyles from "../../common/styles/sports/TeamDetails.styles";
import { useTeamsContext } from "../../../state/teams/context";
import commonStyles from "../../common/styles/Common.styles";
import TeamLogo from "../../team/components/TeamLogo";
import { isDefined } from "../../../util/object-util";

const ABBR_NAME_MAX_LENGTH = 8;

const TeamDetails = ({ team }) => {
	const commonClasses = commonStyles();
	const { state: teamState, saveTeam } = useTeamsContext();
	const homeTeam = teamState.homeTeam();
	const awayTeam = teamState.awayTeam();
	const opponentTeam = team.id === homeTeam.id ? awayTeam : homeTeam;

	const validationSchema = Yup.object({
		abbrName: Yup.string()
			.min(2, "This name is too short")
			.max(ABBR_NAME_MAX_LENGTH, "This name is too long")
			.notRequired()
			.test(
				"not-equal-to-opponent-validation",
				"Value has to be different than opponent's",
				(value) => {
					if (isDefined(value) && isDefined(opponentTeam.abbrName)) {
						return value !== opponentTeam.abbrName;
					}
					return true;
				}
			),
		color: Yup.string()
			.matches(/^#[0-9A-Fa-f]{6}$/, "Invalid color value. Format is: #F0F0F0")
			.required("Required")
			.test(
				"colorAlreadySelected",
				"This hex code is already in use. Please select a different color",
				(value) => {
					return opponentTeam.color !== value;
				}
			)
	});

	const formik = useFormik({
		enableReinitialize: true,
		initialValues: {
			overallRecord: team.overallRecord || "",
			conferenceRecord: team.conferenceRecord || "",
			rank: team.rank || "",
			color: team.color,
			abbrName: team.abbrName || ""
		},
		validationSchema,
		onSubmit: (values) => {
			Object.assign(team, values);
			saveTeam(team);
		}
	});

	const {
		handleSubmit,
		handleChange,
		handleBlur,
		resetForm,
		setFieldValue,
		setTouched,
		isValid,
		errors,
		touched
	} = formik;

	const classes = teamDetailsStyles({
		teamColor: formik.values.color,
		hasLogo: !!team.logo
	});

	return (
		<Box className={commonClasses.container}>
			<Box className={classes.contentContainer}>
				<form className={commonClasses.form} onSubmit={handleSubmit}>
					<Grid
						container
						spacing={5}
						className={classes.detailsContainer}
						alignItems="center"
					>
						<Grid item md={6} container justifyContent="flex-end">
							<Grid item xs={12} sm={12} md={8}>
								<Grid container direction="column" spacing={5} alignItems="center">
									<Grid item sm={12} md={8}>
										<TeamLogo size="large" hideName team={team} />
									</Grid>
									<Grid item sm={12} md={4} className={classes.colorContainer}>
										<TextField
											fullWidth
											variant="outlined"
											id="colorText"
											name="color"
											label="End zone color"
											value={formik.values.color}
											error={touched.color && Boolean(errors.color)}
											helperText={touched.color && errors.color}
											onChange={handleChange}
											onBlur={handleBlur}
										/>
										<ColorPicker
											hideTextfield
											id="color"
											name="color"
											label="End zone color"
											deferred
											disableAlpha
											value={formik.values.color}
											onChange={(color) => {
												setFieldValue("color", `#${color.hex}`);
												setTouched({ color: true }, false);
											}}
										/>
									</Grid>
								</Grid>
							</Grid>
						</Grid>
						<Grid item md={6}>
							<Grid container spacing={5}>
								<Grid item xs={12} sm={12} md={8}>
									<TextField
										id="abbrName"
										name="abbrName"
										label="Abbreviated Name"
										fullWidth
										value={formik.values.abbrName}
										onChange={(e) => {
											setTouched({ abbrName: true });
											handleChange(e);
										}}
										inputProps={{ maxLength: ABBR_NAME_MAX_LENGTH }}
										error={touched.abbrName && Boolean(errors.abbrName)}
										helperText={touched.abbrName && errors.abbrName}
									/>
								</Grid>
								<Grid item xs={12} sm={12} md={8}>
									<TextField
										id="overallRecord"
										name="overallRecord"
										label="Overall Record"
										fullWidth
										value={formik.values.overallRecord}
										onChange={handleChange}
									/>
								</Grid>
								<Grid item xs={12} sm={12} md={8}>
									<TextField
										id="conferenceRecord"
										name="conferenceRecord"
										label="Conference Record"
										fullWidth
										value={formik.values.conferenceRecord}
										onChange={handleChange}
									/>
								</Grid>
								<Grid item xs={12} sm={12} md={8}>
									<TextField
										id="rank"
										name="rank"
										label="Rank"
										fullWidth
										value={formik.values.rank}
										onChange={handleChange}
									/>
								</Grid>
							</Grid>
						</Grid>
					</Grid>
					<Toolbar className={commonClasses.actionToolbar}>
						<Grid container direction="row" justifyContent="flex-end" spacing={2}>
							<Grid item>
								<SecondaryButton color="secondary" label="Cancel" onClick={resetForm} />
							</Grid>
							<Grid item>
								<PrimaryButton
									type="submit"
									color="primary"
									label="Save"
									disabled={!isValid}
								/>
							</Grid>
						</Grid>
					</Toolbar>
				</form>
			</Box>
		</Box>
	);
};

export default TeamDetails;
